<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>PLLG Tracker</h1>
        <Button class="add-btn" label="Add Referral" icon="pi pi-plus" @click="openAddModal" v-if="proxy.$authStore.user?.role?.includes('pllg-staff')" />
      </div>

      <DataTable 
        :value="referrals" 
        :paginator="true" 
        :rows="10"
        :loading="loading"
        stripedRows
        showGridlines
        v-model:filters="filters"
        filterDisplay="menu"
      >
        <template #header>
          <div class="table-header">
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search..." />
            </IconField>
          </div>
        </template>
        <Column field="congress_period" header="Congress" sortable />
        <Column field="date_received" header="Date Received" sortable />
        <Column field="request_hearing_converted" header="Request for Hearing Attendance" sortable />
        <Column field="request_data_converted" header="Request for Data" sortable />
        <Column field="request_position_paper_converted" header="Request for Position Paper / Comments" sortable />

        <Column field="agenda" header="Agenda" sortable />


        <Column field="logged_by" header="Logged By" sortable />
        <Column field="logged_datetime" header="Logged Date & Time" sortable />

        <Column header="Actions" v-if="proxy.$authStore.user?.role?.includes('pllg-staff')">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" :loading="editLoading === data.id" @click="openEditModal(data)"/>
              <Button icon="pi pi-trash" severity="danger" size="small" @click="deleteReferral(data.id)" />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="showModal" :header="isEditing ? 'Edit Referral' : 'Add Referral'" :modal="true" :style="{width: '80vw'}">
      <Form v-slot="$form" :key="formKey" :initialValues :resolver @submit="saveReferral" class="flex flex-col gap-4 w-full sm:w-56">
        <div class="form-grid">
          <p class="required-note"><i class="pi pi-info-circle"></i> All fields marked with * are required</p>
          <div class="form-row">
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="congress_period" name="congress_period" :options="congressOptions" optionLabel="label" optionValue="value" v-model="initialValues.congress_period" showClear />
                <label for="congress_period">Congress Period *</label>
              </FloatLabel>
            </div>
            
            <div class="form-group">
              <FloatLabel>
                <DatePicker id="date_received" name="date_received" dateFormat="yy-mm-dd"/>
                <label for="date_received">Date Received *</label>
              </FloatLabel>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="field-label">Request for Hearing Attendance *</label>
              <SelectButton name="request_hearing" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>

            <div class="form-group">
              <label class="field-label">Request for Data *</label>
              <SelectButton name="request_data" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>

            <div class="form-group">
              <label class="field-label">Request for Position Paper / Comments *</label>
              <SelectButton name="request_position_paper" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <Textarea id="agenda" name="agenda" rows="3"/>
              <label for="agenda">Agenda *</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'Must be in title case format, one grouped agenda per request (consolidated similar agenda bills)'"></i>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputText id="agenda_link" name="agenda_link" v-model="initialValues.agenda_link"/>
              <label for="agenda_link">Agenda Link</label>
            </FloatLabel>
            
          </div>

          <div class="form-row">
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="communication" name="communication" :options="commsOptions" optionLabel="label" optionValue="value" showClear />
                <label for="communication">Communication *</label>
              </FloatLabel>
            </div>

            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="requesting_party" name="requesting_party" :options="requestPartyOptions" optionLabel="label" optionValue="value"  v-model="initialValues.requesting_party" showClear/>
                <label for="requesting_party">Requesting Party *</label>
              </FloatLabel>
            </div>

            <div class="form-group">
              <FloatLabel variant="in">
                
                <Select v-model="initialValues.committee_ngas_op" :options="committeeOptionsGrouped" optionLabel="label" optionValue="value" optionGroupLabel="category" optionGroupChildren="items" filter class="w-full"  />

                <label for="committee_ngas_op">Committee/NGAs/OP Office *</label>
              </FloatLabel>
            </div>

          </div>

          <div v-if="$form.request_position_paper?.value" class="position-paper-section">
            <div class="form-group">
                <label class="field-label">Unnumbered Substitute Bill/Draft Bill *</label>
                <SelectButton id="unnumbered_substitute_bill" name="unnumbered_substitute_bill" :options="yesNoOptions" optionLabel="label" optionValue="value"/>
            </div>
          </div>

          <div v-if="$form.request_hearing?.value" class="hearing-section">
            <div class="form-row">
              <div class="form-group">
                <FloatLabel variant="in">
                  <DatePicker id="hearing_date" name="hearing_date" dateFormat="yy-mm-dd"/>
                  <label for="hearing_date">Hearing Date *</label>
                </FloatLabel>
              </div>

              <div class="form-group">
                <FloatLabel variant="in">
                  <DatePicker id="hearing_time" name="hearing_time" hourFormat="12" timeOnly />
                  <label for="hearing_time">Hearing Time *</label>
                </FloatLabel>
              </div>
            </div>

            <div class="form-group">
              <FloatLabel variant="in">
                <InputText id="hearing_place"name="hearing_place"/>
                <label for="hearing_place">Hearing Place *</label>
              </FloatLabel>
            </div>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <Select id="notice_cancellation" name="notice_cancellation" :options="rescheduledOptions" optionLabel="label" optionValue="value" showClear  />
              <label for="notice_cancellation">Notice of Cancellation/Reschedule</label>
            </FloatLabel>
          </div>

          <div class="form-group">
            <FloatLabel variant="in">
              <MultiSelect id="referral" name="referral" :options="referralOptions" optionLabel="label" optionValue="value" optionGroupLabel="category" optionGroupChildren="items" filter showClear/>
              <label for="referral">Referral *</label>
            </FloatLabel>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <Textarea id="confirmed_attendees" name="confirmed_attendees" rows="2" />
              <label for="confirmed_attendees">Confirmed Attendees</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'Preferably Identified DLLOs (Director Level) that confirmed their attendance'"></i>
          </div>

          <div class="form-group">
            <FloatLabel variant="in">
              <DatePicker id="deadline" name="deadline" dateFormat="yy-mm-dd" />
              <label for="deadline">Deadline for Position Papers / Comments / Data</label>
            </FloatLabel>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputChips id="house_bill_no" name="house_bill_no" v-model="initialValues.house_bill_no" separator="," addOnBlur />
              <label for="house_bill_no">House Bill No</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'must only contain numbers'"></i>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputChips id="senate_bill_no" name="senate_bill_no" v-model="initialValues.senate_bill_no" separator="," addOnBlur />
              <label for="senate_bill_no">Senate Bill No</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'must only contain numbers'"></i>
          </div>

          <div class="form-group field-with-info">
            <FloatLabel variant="in">
              <InputChips id="proposed_resolution_no" name="proposed_resolution_no" v-model="initialValues.proposed_resolution_no" separator="," addOnBlur />
              <label for="proposed_resolution_no">Proposed Resolution No</label>
            </FloatLabel>
            <i class="pi pi-info-circle info-icon" v-tooltip="'must only contain numbers'"></i>
           
          </div>

          <div class="form-group  field-with-info">
            <FloatLabel variant="in">
              <InputChips id="other"  name="other" v-model="initialValues.other" separator="," addOnBlur />
              <label for="other">Other</label>
            </FloatLabel>
             <i class="pi pi-info-circle info-icon" v-tooltip="'ex. Proposed Joint Resolution No. 10, Privilege Speech of Sen. Hontiveros on Online Gambling dated 9 September 2025'"></i>
          </div>

        </div>
        
        <div class="modal-actions">
          <Button type="button" label="Cancel" severity="secondary" icon="pi pi-times" @click="closeModal" />
          <Button type="submit" :label="isEditing ? 'Update' : 'Create'" icon="pi pi-check" :loading="saving" />
        </div>
      </Form>
    </Dialog>
  </div>
</template>


<script setup>

/**
 * @fileoverview PLLG Tracker - Referrals Management Component
 * 
 * This component manages the PLLG (Policy and Legislative Liaison Group) tracker system
 * for tracking legislative referrals, hearing requests, and position paper requirements.
 * 
 * @component Referrals
 * @requires vue
 * @requires @primevue/forms
 * @requires sweetalert2
 * @requires ../axios.js
 * @requires ../assets/committees-agencies.json
 * 
 * @description
 * Features:
 * - View all referrals in a paginated, searchable data table
 * - Create new referrals with comprehensive form validation
 * - Edit existing referrals
 * - Delete referrals with confirmation
 * - Role-based access control (pllg-staff only can add/edit/delete)
 * - Input sanitization for XSS protection
 * - Conditional form fields based on request types
 * 
 * @security
 * - Input sanitization removes HTML tags (<>) from text inputs
 * - Array inputs are sanitized element by element
 * - Role-based UI restrictions for CRUD operations
 * 
 * @author LMP Development Team
 * @version 1.0.0
 */
 

import { ref, onMounted, getCurrentInstance, watch  } from 'vue'
import { Form } from '@primevue/forms'
import Swal from 'sweetalert2'
import api from '../axios.js'


const { proxy } = getCurrentInstance()

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

/**
 * List of all referrals
 */
const referrals = ref([])

/**
 * Controls modal visibility
 */
const showModal = ref(false)

/**
 * Indicates if form is in edit mode
 */
const isEditing = ref(false)

/**
 * Indicates if save operation is in progress
 */
const saving = ref(false)
const formKey = ref(0)

/**
 * Indicates if data is being loaded
 */
const loading = ref(false)
const editLoading = ref(null)

/**
 * Available referral office options
 */
const referralOptions = ref([])

/**
 * Filter global settings
 */
const filters = ref({
  global: { value: null, matchMode: 'contains' }
})
// ============================================================================
// FORM OPTIONS
// ============================================================================

/**
 * Yes/No options for boolean fields
 * @constant
 */
const yesNoOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
]

/**
 * Grouped committee options from JSON data
 * @constant
 */
const committeeOptionsGrouped = ref([])

const loadCommitteesAgencies = async (congressNo, requestingParty) => {
  try {
    const response = await api.get(`/committees-agencies-grouped?congress_no=${congressNo}&requesting_party=${requestingParty}`)
    committeeOptionsGrouped.value = response.data.map(group => ({
      category: group.label,
      items: group.items.map(item => ({ label: item.name, value: item.name }))
    }))
  } catch (error) {
    console.error('Error loading committees/agencies:', error)
  }
}

/**
 * Congress period options
 * @constant
 */
const congressOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' }
]

/**
 * Communication options
 * @constant
 */
const commsOptions = [
  { label: 'Formal (Email/Memo/Letter)', value: 'Formal (Email/Memo/Letter)' },
  { label: 'Informal (Word of Mouth, Message, Call, Viber)', value: 'Informal (Word of Mouth, Message, Call, Viber)' },
]

/**
 * Requesting Party options
 * @constant
 */
const requestPartyOptions = [
  { label: 'House', value: 'HOUSE' },
  { label: 'Senate', value: 'SENATE' },
  { label: 'National Government Agency', value: 'NGA' },
  { label: 'DOF Attached Agency', value: 'DAA' },
  { label: 'DOF Office', value: 'DO' },
  { label: 'Others', value: 'OTHERS' }
]

/**
 * Reschedule options
 * @constant
 */
const rescheduledOptions = [
  { label: 'Rescheduled', value: 'RESCHEDULED' },
  { label: 'Postponed', value: 'POSTPONED' },
  { label: 'Cancelled', value: 'CANCELLED' }
]

/**
 * Form initial values
 * @constant
 */
const initialValues = ref({
  id: null,
  congress_period: '',
  date_received: null,
  request_hearing: false,
  request_data: false,
  request_position_paper: false,
  agenda: null,
  agenda_link: null,
  communication: '',
  requesting_party: '',
  committee_ngas_op: '',
  notice_cancellation: '',
  referral: '',
  confirmed_attendees: '',
  deadline: null,
  house_bill_no: [],
  senate_bill_no: [],
  proposed_resolution_no: [],
  other: [],
  hearing_date: null,
  hearing_time: null,
  hearing_place: '',
  unnumbered_substitute_bill: false
})


// ============================================================================
// FORM VALIDATION
// ============================================================================
/**
 * Form validation resolver
 * @param {Object} params - Validation parameters
 * @param {Object} params.values - Form values to validate
 * @returns {Object} Validation result with values and errors
 */
const resolver = ({ values }) => {
    const errors = {};
    const required = ['congress_period', 'date_received', 'agenda', 'communication','requesting_party','committee_ngas_op','referral'];
    
    // Validate required fields
    required.forEach(field => {
        if (!values[field]) errors[field] = [{ message: '' }];
    });

    // Validate boolean fields
    if (values.request_hearing === null || values.request_hearing === undefined) {
        errors.request_hearing = [{ message: '' }];
    }
    if (values.request_data === null || values.request_data === undefined) {
        errors.request_data = [{ message: '' }];
    }
    if (values.request_position_paper === null || values.request_position_paper === undefined) {
        errors.request_position_paper = [{ message: '' }];
    }

    // Conditional validation for hearing details
    if (values.request_hearing) {
        if (!values.hearing_date) errors.hearing_date = [{ message: '' }];
        if (!values.hearing_time) errors.hearing_time = [{ message: '' }];
        if (!values.hearing_place) errors.hearing_place = [{ message: '' }];
    }

    // Conditional validation for position paper
    if (values.request_position_paper) {
        if (values.unnumbered_substitute_bill === null || values.unnumbered_substitute_bill === undefined) {
            errors.unnumbered_substitute_bill = [{ message: '' }];
        }
    }

    return { values, errors };
};


// ============================================================================
// INPUT SANITIZATION
// ============================================================================
/**
 * Sanitizes string input by removing HTML tags and trimming whitespace
 * @param {string|*} input - Input to sanitize
 * @returns {string|*} Sanitized input or original if not a string
 */
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input
  return input.replace(/[<>]/g, '').trim()
}

/**
 * Sanitizes array of strings
 * @param {Array} arr - Array to sanitize
 * @returns {Array} Sanitized array
 */
const sanitizeArray = (arr) => {
  if (!Array.isArray(arr)) return []
  return arr.map(item => sanitizeInput(item))
}

// ============================================================================
// DATA LOADING
// ============================================================================

/**
 * Loads all referrals from API
 * @async
 * @returns {Promise<void>}
 */
const loadReferrals = async () => {
  try {
    loading.value = true
    const response = await api.get('/pllg-tracker')
    referrals.value = response.data
  } catch (error) {
    console.error('Error loading referrals:', error)
  } finally {
    loading.value = false
  }
}

/**
 * Loads referral office options from API
 * @async
 * @returns {Promise<void>}
 */
const loadReferralOptions = async () => {
  try {
    const response = await api.get('/agencies-grouped')
    referralOptions.value = response.data.map(group => ({
      category: group.label,
      items: group.items.map(item => ({ label: item.name, value: item.code }))
    }))
  } catch (error) {
    console.error('Error loading referral options:', error)
  }
}

// ============================================================================
// MODAL OPERATIONS
// ============================================================================

/**
 * Opens modal for adding new referral
 * Resets form to initial state
 */
const openAddModal = () => {
  isEditing.value = false
  initialValues.value = {
    id: null,
    congress_period: '',
    date_received: null,
    request_hearing: false,
    request_data: false,
    request_position_paper: false,
    logged_by: '',
    logged_datetime: new Date(),
    agenda: '',
    communication: '',
    requesting_party: '',
    committee_ngas_op: '',
    notice_cancellation: '',
    referral: '',
    confirmed_attendees: '',
    deadline: null,
    house_bill_no: [],
    senate_bill_no: [],
    proposed_resolution_no: [],
    other: [],
    hearing_date: null,
    hearing_time: null,
    hearing_place: '',
    unnumbered_substitute_bill: false
  }
  formKey.value++
  showModal.value = true
}

/**
 * Opens modal for editing existing referral
 * @param {Object} referral - Referral data to edit
 */
const openEditModal = async (referral) => {
  editLoading.value = referral.id
  isEditing.value = true
  const data = { ...referral }
  if (typeof data.committee_ngas_op === 'string') {
    // data.committee_ngas_op = data.committee_ngas_op ? [data.committee_ngas_op] : ''
  }
  initialValues.value = data
  initialValues.value._originalCommittee = data.committee_ngas_op
  initialValues.value._originalRequestingParty = data.requesting_party
  if (referral.congress_period && referral.requesting_party) {
    await loadCommitteesAgencies(referral.congress_period, referral.requesting_party)
  }
  formKey.value++
  editLoading.value = null
  showModal.value = true
}

/**
 * Closes modal and resets saving state
 */
const closeModal = () => {
  showModal.value = false
  saving.value = false
}

// ============================================================================
// CRUD OPERATIONS
// ============================================================================

/**
 * Saves referral (create or update)
 * @async
 * @param {Object} params - Form submission parameters
 * @param {boolean} params.valid - Form validation status
 * @param {Object} params.values - Form values
 * @returns {Promise<void>}
 */
const saveReferral = async ({ valid, values }) => {

  // Sanitize all inputs
  const payload = {
    ...values,
    id: initialValues.value.id,
    congress_period: sanitizeInput(values.congress_period),
    agenda: sanitizeInput(values.agenda),
    agenda_link: sanitizeInput(values.agenda_link),
    confirmed_attendees: sanitizeInput(values.confirmed_attendees),
    hearing_place: sanitizeInput(values.hearing_place),
    committee_ngas_op: sanitizeInput(initialValues.value.committee_ngas_op),
    requesting_party: sanitizeInput(values.requesting_party),
    communication: sanitizeInput(values.communication),
    house_bill_no: sanitizeArray(initialValues.value.house_bill_no),
    senate_bill_no: sanitizeArray(initialValues.value.senate_bill_no),
    proposed_resolution_no: sanitizeArray(initialValues.value.proposed_resolution_no),
    other: sanitizeArray(initialValues.value.other)
  };

    if(valid) {
      // Determine if email will be sent
      let confirmText = `Are you sure you want to ${isEditing.value ? 'update' : 'create'} this referral?`
      if (!isEditing.value) {
        confirmText += '\n\nAn email notification will be sent to the assigned Liaison Officer/s.'
      } else if (initialValues.value._originalCommittee !== initialValues.value.committee_ngas_op || initialValues.value._originalRequestingParty !== values.requesting_party) {
        confirmText += '\n\nThe committee/requesting party has changed. An email notification will be sent to the newly assigned Liaison Officer/s.'
      }

      const result = await Swal.fire({
        title: 'Confirm Action',
        text: confirmText,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Proceed',
        cancelButtonText: 'Cancel'
      })
      
      if (!result.isConfirmed) return
      
      try {
        saving.value = true

        // API call
        if (isEditing.value) {
          await api.put(`/pllg-tracker/${payload.id}`, payload)
        } else {
          await api.post('/pllg-tracker', payload)
        }
        
        closeModal()
        loadReferrals()
        
        // Success notification
        Swal.fire({
          title: 'Success!',
          text: `Referral ${isEditing.value ? 'updated' : 'created'} successfully`,
          icon: 'success'
        })
      } catch (error) {

        // Error notification
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || `Failed to ${isEditing.value ? 'update' : 'create'} referral`,
          icon: 'error'
          })
        } finally {
          saving.value = false
        }

    }

}

/**
 * Deletes referral with confirmation
 * @async
 * @param {number} id - Referral ID to delete
 * @returns {Promise<void>}
 */
const deleteReferral = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Referral',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  
  if (result.isConfirmed) {
    try {
      await api.delete(`/pllg-tracker/${id}`)
      loadReferrals()
      Swal.fire('Deleted!', 'Referral deleted successfully', 'success')
    } catch (error) {
      console.error('Error deleting referral:', error)
    }
  }
}

// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================

watch(() => initialValues.value.requesting_party, (newVal, oldVal) => {
  if (newVal) loadCommitteesAgencies(initialValues.value.congress_period, newVal)
  if (oldVal && oldVal !== newVal) {
    initialValues.value.committee_ngas_op = []
  }
})

/**
 * Component mounted hook
 * Loads initial data
 */
onMounted(() => {
  loadReferrals()
  loadReferralOptions()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}

.container {
  /* background: rgba(255, 255, 255, 0.95); */
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin: 1rem;
  padding: 1.5rem;
  /* box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); */
  width: calc(100vw - 2rem);
  max-width: none;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.add-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #1e40af;
}

.meeting-tabs {
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
}

.tab-btn {
  background: none;
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #1e3a8a;
}

.tab-btn.active {
  color: #1e3a8a;
  border-bottom-color: #1e3a8a;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

.chamber-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.chamber-badge.senate {
  background: #dbeafe;
  color: #2563eb;
}

.chamber-badge.house {
  background: #d1fae5;
  color: #065f46;
}

.chamber-badge.other {
  background: #fef3c7;
  color: #d97706;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.scheduled {
  background: #e0e7ff;
  color: #3730a3;
}

.status-badge.ongoing {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.completed {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.cancelled {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.postponed {
  background: #f3e8ff;
  color: #7c3aed;
}

.link-btn {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.link-btn:hover {
  text-decoration: underline;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn, .delete-btn {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.edit-btn {
  background: #3b82f6;
  color: white;
}

.edit-btn:hover {
  background: #2563eb;
}

.delete-btn {
  background: #ef4444;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

.modal-form {
  padding: 0;
}

.required-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  color: #1e40af;
  font-size: 0.9rem;
}

.required-note i {
  color: #3b82f6;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.field-label {
  font-weight: 600;
  color: #828fa1;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.button-group button {
  flex: 1;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.field-with-info {
  position: relative;
}

.info-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  color: #3b82f6;
  cursor: help;
  font-size: 1rem;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-dropdown,
.form-group .p-datepicker,
.form-group .p-MultiSelect {
  width: 100% !important;
  box-sizing: border-box;
}

.form-group :deep(.p-select),
.form-group :deep(.p-multiselect) {
  min-height: 3.5rem;
}

.form-group .p-datepicker .p-inputtext,
.form-group .p-dropdown .p-dropdown-label,
.form-group .p-MultiSelect .p-MultiSelect-label {
  width: 100% !important;
}

/* Additional specificity for PrimeVue components */
.form-group .p-component {
  width: 100% !important;
}

.form-group .p-element {
  width: 100% !important;
}

/* Specific rules for MultiSelect component */
.form-group .p-MultiSelect {
  width: 100% !important;
  min-width: 0 !important;
}

.form-group .p-MultiSelect .p-MultiSelect-label {
  width: 100% !important;
}

.form-group .p-MultiSelect .p-MultiSelect-trigger {
  width: auto !important;
}

.form-group .p-inputchips {
  width: 100% !important;
  height: 60px !important;
 
}

.form-group :deep(.p-inputchips .p-inputchips-input-container) {
  padding-top: 2rem !important;
  min-height: 4rem !important;
}

.form-group :deep(.p-inputchips-chip) {
  padding-top: 1.2rem !important;
}

.form-group :deep(.p-floatlabel.p-variant-in:has(.p-inputchips) label) {
  top: 0.5rem !important;
  font-size: 0.75rem !important;
  transform: translateY(0) !important;
}

.form-group :deep(.p-floatlabel.p-variant-in .p-inputchips) {
  padding-top: 0 !important;
}

.hearing-section {


  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.hearing-section h3 {
  color: #1e3a8a;
  margin: 0;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .committee-meetings-page {
    padding: 1rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal {
    width: 95%;
  }
}
</style>
